import {
  Box,
  Heading,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  Divider,
  Text,
  TableContainer,
  IconButton,
  Link,
} from '@chakra-ui/react';
import React, {useState, useEffect, useRef} from 'react';
import FlexCard from '../../components/General/FlexCard';
import {useForm} from 'react-hook-form';
import {CustomButton, CustomSelect, DatePicker, SubmitButton} from '../../components/General/FormInputs';
import {FileDropper} from '../../components/Reports/FileDropper';
import {postUpload, get, postDelete, post, queryStringBuilder, getFile} from '../../service/ApiService';
import {Batch, File} from '../../service/Urls';
import ErrorHelper from '../../service/ErrorHelper';
import '../../styles/reports.css';
import {FaEdit} from 'react-icons/fa';
import {useNavigate} from 'react-router-dom';
import ServiceTypeList from '../../components/General/ServiceTypeList';
import {useTitle} from '../../components/General/Layout';
import {Report} from '../../service/Urls';
import '../../styles/table.css';

import {formatDateForPicker} from '../../helpers/Functions';

export const ImportSpreadsheets = () => {
  const getToday = () => {
    return formatDateForPicker(new Date());
  };

  const errorTypes = Object.freeze({ARRANGER: 1, LOCATION: 2, SECONDARY_ARRANGER: 3});
  const [files, setFiles] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [toast, setToast] = useState(undefined);
  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [batchRecords, setBatchRecords] = useState([]);
  const [batchErrors, setBatchErrors] = useState([]);
  const [batchError, setBatchError] = useState(null);
  const [elements, setElements] = useState([]);
  const [title, setTitle] = useTitle('Maintenance - Import Spreadsheets');
  const navigate = useNavigate();
  const [pages, setPages] = useState({
    pageNum: 1,
    totalPages: 1,
    itemsPerPage: 500,
    getNewPage: curPage => {
      loadData(curPage);
    },
  });

  const [pdfUrl, setPdfUrl] = useState([]);
  const linkRef = useRef(null);

  const {isOpen, onOpen, onClose} = useDisclosure();
  const {isOpen: resolveIsOpen, onOpen: resolveOnOpen, onClose: resolveOnClose} = useDisclosure();
  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    loadTitle();
    refresh();
  }, []);

  useEffect(() => {
    refresh();
  }, [startDate, endDate]);

  useEffect(() => {
    if (batchError) {
      resolveOnOpen();
    }
  }, [batchError]);

  // for when we get a report back
  useEffect(() => {
    if (pdfUrl.length > 0) linkRef.current.click();
  }, [pdfUrl]);

  const loadTitle = () => {
    setTitle('Maintenance - Import Spreadsheets');
  };

  const inputRef = useRef('');
  const handleChange = newFiles => {
    setFiles([]);
    const nFiles = [];
    if (newFiles.length == 1 && (newFiles[0].name.toLowerCase().endsWith('.xls') || newFiles[0].name.toLowerCase().endsWith('.xlsx'))) {
      nFiles.push(newFiles[0]);
      setIsDisabled(false);
    } else {
      for (let x = 0; x < newFiles.length; x++) {
        if (newFiles[x].name.toLowerCase().endsWith('.xls') || newFiles[x].name.toLowerCase().endsWith('.xlsx')) {
          nFiles.push(newFiles[x]);
        } else {
          setToast({response: {error: true}, errorMessage: 'The file "' + newFiles[x].name + '" is not an excel file'});
        }
      }
      setIsDisabled(!nFiles.length > 0);
    }
    setFiles(nFiles);
  };

  const importClient = async data => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    const response = await postUpload(File.UPLOAD + data.ServiceType, formData);
    setToast({response: response, successMessage: 'Successfully uploaded ' + (files.length > 1 ? files.length + ' files' : 'file')});
    setFiles([]);
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const getBatchList = async data => {
    if (!data.ServiceType) data.ServiceType = 1;
    // data.type = 1;
    const response = await get(File.RESULTS + data.ServiceType + '?StartDate=' + startDate + '&EndDate=' + endDate);
    if (response.data) {
      setBatchRecords(response.data);
    }
  };

  const refresh = async () => {
    const data = getValues();
    await getBatchList(data);
  };

  const deleteBatch = async id => {
    if (await confirm('Are you sure you want to delete the batch?')) {
      const response = await postDelete(Batch.DELETEBATCH + id);
      setToast({response: response, successMessage: 'Successfully deleted '});
      setTimeout(() => {
        refresh();
      }, 1000);
      // getClient(client.id);
    }
  };

  const ExtraColumns = ({batch, status}) => {
    return (
      <>
        <Td title={status.includes('Errors') ? 'Multiple Errors' : status.includes('Error') ? 'No Resolvable Errors' : 'No Errors'}>
          {status.includes('Errors') ? (
            <CustomButton
              name="View Errors"
              onClick={() => {
                getErrors(batch);
                onOpen();
              }}
            />
          ) : status.includes('Error') ? (
            <Text>No Resolvable Errors</Text>
          ) : (
            <Text>No Errors</Text>
          )}
        </Td>
        <Td>
          <CustomButton
            name="Delete"
            onClick={() => {
              deleteBatch(batch);
            }}
          />
        </Td>
      </>
    );
  };

  const getStatus = (status, exceptionCount) => {
    let name;
    switch (status) {
      case 0:
        name = 'Queued';
        break;
      case 1:
        name = 'Processing';
        break;
      case 2:
        name = 'Complete';
        break;
      case 3:
        name = exceptionCount > 0 ? 'Complete With Errors' : 'Complete with Error';
        break;
      case 4:
        name = exceptionCount > 0 ? 'Errors' : 'Error';
        break;
    }
    return name;
  };

  const getErrors = async id => {
    const response = await get(File.ERRORS + id);
    if (response.data.length > 1 || response.data) {
      setBatchErrors(response.data);
    } else {
      setBatchErrors([response]);
    }
  };

  const edit = masterId => {
    navigate('/maintenance/master/edit', {state: {id: masterId}});
  };

  const addNew = async id => {
    const response = await get(File.ERRORS + id);
    if (response.data.length > 1 || response.data) {
      setBatchErrors(response.data);
    } else {
      setBatchErrors([response]);
    }
  };

  const transformText = text => {
    if (canResolve(text)) {
      if (text.split('|').length > 1) {
        return text.split('|').map((item, index) => {
          return (
            <Text key={index + item}>
              {item}
              <br />
            </Text>
          );
        });
      }
    }
    return <Text>{text}</Text>;
  };
  const transformError = text => {
    if (text.split(':').length > 1) {
      return text.split(':').map((item, index) => {
        return (
          <Text key={index + 'tE' + item}>
            {item}
            {index === 0 ? ':' : ''}
            <br />
          </Text>
        );
      });
    }
  };

  const getErrorType = text => {
    let type = 0;
    if (text.toLowerCase().includes('secondary arranger not present')) {
      type = errorTypes.SECONDARY_ARRANGER;
    } else if (text.toLowerCase().includes('location not present')) {
      type = errorTypes.LOCATION;
    } else if (text.toLowerCase().includes('arranger not present')) {
      type = errorTypes.ARRANGER;
    }
    return type;
  };

  const verifyAlias = text => {
    const values = getValues();
    const type = getErrorType(text);
    if (type === errorTypes.ARRANGER && values['arId']) {
      return values['arId'];
    } else if (type === errorTypes.LOCATION && values['loId']) {
      return values['loId'];
    } else if (type === errorTypes.SECONDARY_ARRANGER && values['secArId']) {
      return values['secArId'];
    }
    return null;
  };

  // Gross, but check if error text contains message and determine what type to send.
  const resolveError = async (errorText, isAdd = true) => {
    const type = getErrorType(errorText);
    const data = {
      type: type,
      smasterid: batchError.sMasterId,
    };

    // If not adding, then we need to verify that an alias was selected.
    if (!isAdd) {
      const aliasId = verifyAlias(errorText);
      if (aliasId !== null) {
        data.aliasid = aliasId;
      } else {
        setToast({
          response: {error: true},
          errorMessage: 'You must select ' + (type === errorTypes.LOCATION ? 'a location' : 'an arranger') + ' before adding a new alias.',
        });
        return;
      }
    }

    const url = isAdd ? File.CREATERECORD : File.CREATEALIAS;
    const response = await post(url, data);
    const successMessage = isAdd ? 'Successfully added record' : 'Successfully added alias ';
    setToast({response: response, successMessage: successMessage});
    if (response.isSuccess) {
      await refresh();
      resolveOnClose();
      onClose();
    }
  };

  // Check if error message can be resolved or not by checking if it contains a particular arranger/location message
  const canResolve = text => {
    const type = getErrorType(text);
    if (type === errorTypes.ARRANGER || type === errorTypes.LOCATION || type === errorTypes.SECONDARY_ARRANGER) {
      return true;
    }
    return false;
  };

  const AliasDropdown = props => {
    const [dropdownData, setDropdownData] = useState([]);
    const [id, setId] = useState('');
    const [name, setName] = useState(null);
    const [header, setHeader] = useState('');
    useEffect(() => {
      populateData();
    }, []);

    async function populateData() {
      const type = getErrorType(props.text);
      const header = type === errorTypes.ARRANGER ? 'Arranger' : type === errorTypes.LOCATION ? 'Location' : 'Secondary Arranger';
      setHeader(header);

      if (type === errorTypes.LOCATION) {
        const retVal = await get('/Location/dropdown-list/' + batchError.clientId);
        // filter locations to ensure they have a loName
        const filteredLocations = retVal.data.filter(location => location.loName && location.loName !== null);
        setDropdownData(filteredLocations);
        setId('loId');
        setName('loName');
      } else {
        // otherwise we're dealing with arrangers
        if (type === errorTypes.SECONDARY_ARRANGER) {
          setId('secArId');
        } else {
          setId('arId');
        }
        const retVal = await get('/Arranger/dropdown-list/' + batchError.clientId);
        setDropdownData(retVal.data);
        setName('arrangerName');
      }
    }

    return (
      dropdownData.length > 0 &&
      name && (
        <CustomSelect id={id} register={register} errors={errors} name={`Select ${header}`} required={true} defaultValue={dropdownData[0].npk}>
          {dropdownData?.map(item => {
            return (
              <option key={item.npk} value={item.npk}>
                {(item[id] ?? '') + ' | ' + item[name] + ' | ' + (item.loRptname ?? '')}
              </option>
            );
          })}
        </CustomSelect>
      )
    );
  };

  const printReport = async (fileName, batchId) => {
    const params = {
      StartDate: '2020-01-01',
      EndDate: '2020-12-31',
      BatchId: batchId,
    };

    const url = queryStringBuilder(`${Report.DEFAULT_REPORT}${fileName}`, params);
    // const url = queryStringBuilder(SurveyPrints.reportbuttonFile, params);
    const response = await getFile(url);
    if (response.error) {
      setToast({response: {error: 1}, errorMessage: 'Error getting report: ' + response.error});
      return;
    }

    const fileURL = window.URL.createObjectURL(response);

    if (!response.error) {
      setPdfUrl(fileURL);
    }
  };

  return (
    <Box>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      <Heading m="2">Import Spreadsheets</Heading>
      <FlexCard w="90%" m="auto" minW="200px" p="20">
        <Stack mr="auto" w="100%" justify="start">
          <form onSubmit={handleSubmit(importClient)}>
            <Box>
              <Heading size="md" mb="3" textAlign="start">
                Survey type
              </Heading>
              <ServiceTypeList control={control} />
            </Box>
            <Divider my="3" />
            <FileDropper inputRef={inputRef} multiple={true} state={files} setState={setFiles} handleChange={handleChange} />
            <Box>
              <SubmitButton id="formButton" disabled={isDisabled} name="Import Batch" />
            </Box>
          </form>
          <Divider />
          <form id="batchList" onSubmit={handleSubmit(getBatchList)}>
            <Flex my="2">
              <DatePicker
                value={startDate}
                onChange={e => {
                  if (e.target.value instanceof Date) {
                    setStartDate(formatDateForPicker(new Date(e.target.value)));
                  } else {
                    setStartDate(e.target.value);
                  }
                }}
                register={register}
                id="startDate"
                name="Start Date"
                errors={errors}
                required
              />
              <DatePicker
                value={endDate}
                onChange={e => {
                  if (e.target.value instanceof Date) {
                    setEndDate(formatDateForPicker(new Date(e.target.value)));
                  } else {
                    setEndDate(e.target.value);
                  }
                }}
                register={register}
                id="endDate"
                name="End Date"
                errors={errors}
                required
              />
              <SubmitButton style={{marginTop: '19px'}} name="Refresh" />
            </Flex>
          </form>
          <TableContainer width={{base: '100%'}}>
            <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'} sx={{tableLayout: 'fixed'}}>
              <Thead>
                <Tr border="solid 2px #0c5f78">
                  <Th>
                    <Text>File Name</Text>
                  </Th>
                  <Th>
                    <Text>Batch #</Text>
                  </Th>
                  <Th>
                    <Text>Date Added</Text>
                  </Th>
                  <Th>
                    <Text>Status</Text>
                  </Th>
                  <Th align="center" textAlign="center">
                    <Text>Records</Text>
                  </Th>
                  <Th align="center" textAlign="center">
                    <Text>Duplicates</Text>
                  </Th>
                  <Th align="center" textAlign="center">
                    <Text>Exceptions</Text>
                  </Th>
                  <Th>
                    <Text>File Error</Text>
                  </Th>
                  <Th>
                    <Text>Status</Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {batchRecords.map(record => (
                  <Tr key={record.batchNumber}>
                    <Td title={record.fileName}>
                      <Text>{record.fileName}</Text>
                    </Td>
                    <Td>
                      <Text title={record.batchNumber}>{record.batchNumber}</Text>
                    </Td>
                    <Td title={record.dateAdded.replaceAll('-', '/').substring(0, record.dateAdded.indexOf('T'))}>
                      <Text>{record.dateAdded.replaceAll('-', '/').substring(0, record.dateAdded.indexOf('T'))}</Text>
                    </Td>
                    <Td title={getStatus(record.status, record.exceptionCount)}>
                      <Text>{getStatus(record.status, record.exceptionCount)}</Text>
                    </Td>
                    <Td align="center" textAlign="center">
                      <Text>{record.recordCount}</Text>
                    </Td>
                    <Td align="center" textAlign="center">
                      {record.duplicateCount ? (
                        <Link color="blue.600" textDecor="underline" onClick={() => printReport('batch02bm.rdlx', record.batchId)}>
                          {record.duplicateCount}
                        </Link>
                      ) : (
                        <Text>{record.duplicateCount}</Text>
                      )}
                    </Td>
                    <Td align="center" textAlign="center">
                      {record.exceptionCount ? (
                        <Link color="blue.600" textDecor="underline" onClick={() => printReport('batchexceptions.rdlx', record.batchId)}>
                          {record.exceptionCount}
                        </Link>
                      ) : (
                        <Text>{record.exceptionCount}</Text>
                      )}
                    </Td>
                    <Td title={record.fileError}>
                      <Text>{record.fileError}</Text>
                    </Td>
                    <ExtraColumns id={record.id} batch={record.batchId ?? ''} status={getStatus(record.status, record.exceptionCount)} />
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
      </FlexCard>
      <Modal blockScrollOnMount={false} size={'4xl'} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width={{base: '500px', md: '800px', lg: '900px'}}>
          <ModalHeader id="header">{batchErrors.length} Errors</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
            <TableContainer width={{base: '100%'}}>
              <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                <Thead>
                  <Tr border="solid 2px #0c5f78">
                    <Th>
                      <Text>Record Number</Text>
                    </Th>
                    <Th>
                      <Text>Error Message</Text>
                    </Th>
                    <Th>
                      <Text>Edit and Resolve</Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {batchErrors.map((batch, index) => {
                    return (
                      <Tr key={batch.recordNumber + 'bE' + index}>
                        <Td>{batch.recordNumber}</Td>
                        <Td>{transformText(batch.errorMessage)}</Td>
                        <Td>
                          <IconButton title="Edit" colorScheme="secondary" size="md" icon={<FaEdit />} mr="2" onClick={() => edit(batch.sMasterId)} />
                          {canResolve(batch.errorMessage) ? (
                            <CustomButton
                              name="Resolve"
                              mr={3}
                              w="60%"
                              onClick={() => {
                                setBatchError(batch);
                                resolveOnOpen();
                              }}
                            />
                          ) : (
                            <Text></Text>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>

          <ModalFooter>
            <CustomButton name="Close" mr={3} onClick={onClose} />
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal blockScrollOnMount={false} size={'5xl'} isOpen={resolveIsOpen} onClose={resolveOnClose}>
        <ModalOverlay />
        <ModalContent width={{base: '600px', md: '900px', lg: '1000px'}}>
          {batchError && <ModalHeader id="header">Record #{batchError.recordNumber}</ModalHeader>}
          <ModalCloseButton />
          <ModalBody>
            <TableContainer width={{base: '100%'}}>
              <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'sm'}>
                <Thead>
                  <Tr border="solid 2px #0c5f78">
                    <Th>
                      <Text>Error</Text>
                    </Th>
                    <Th>
                      <Text>Add new</Text>
                    </Th>
                    <Th />
                    <Th>
                      <Text>Selected Alias</Text>
                    </Th>
                    <Th>
                      <Text>Assign Alias</Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {batchError &&
                    batchError.errorMessage.split('|').map((text, index) => {
                      if (text === '') return;
                      return (
                        <Tr key={index + 'T' + text}>
                          <Td>{transformError(text)}</Td>

                          <Td>
                            <CustomButton
                              key={index + 'B'}
                              name={'Add as new'}
                              onClick={() => {
                                resolveError(text, true);
                              }}
                            />
                          </Td>
                          <Td>
                            <Text fontWeight={'bold'}>or</Text>
                          </Td>
                          <Td>
                            <AliasDropdown text={text} />
                          </Td>
                          <Td>
                            <Box key={index + 'BA'}>
                              <CustomButton name={'Assign existing alias'} onClick={() => resolveError(text, false)} />
                            </Box>
                          </Td>
                        </Tr>
                      );
                    })}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>

          <ModalFooter>
            <CustomButton name="Close" mr={3} onClick={resolveOnClose} />
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ErrorHelper toastResponse={toast} />
    </Box>
  );
};
/* library used: https://www.npmjs.com/package/react-file-drop
 */
